import React, { useEffect, useState, useCallback, createContext, useContext } from 'react';
import { Box, CircularProgress } from '@mui/material';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Prod default unchanged; the hub build sets REACT_APP_REGISTRATION_BASE_URL (plan 2124).
const DEFAULT_REGISTRATION_BASE_URL = process.env.REACT_APP_REGISTRATION_BASE_URL || 'https://events.forticloudcse.com/register';
// Origin of the self-service Events site; GET /auth/me serves the per-environment value as `hub_url`.
const DEFAULT_HUB_URL = 'https://events.forticloudcse.com';

const DEV_USER = {
  username: 'dev', realm: 'local', groups: [],
  role: 'admin',
  registration_base_url: process.env.REACT_APP_REGISTRATION_BASE_URL || DEFAULT_REGISTRATION_BASE_URL,
  hub_url: DEFAULT_HUB_URL,
  audience_write_enabled: true,
  permissions: {
    dashboard: ['read'], events: ['read'], impact: ['read'],
    tag_analytics: ['read'], help: ['read'], exec: ['read', 'write'],
    'admin.tags': ['read', 'write'], 'admin.content': ['read', 'write'],
    'admin.images': ['read', 'write'], 'admin.api_tools': ['read', 'write'],
    'admin.access': ['read', 'write'], self: ['read', 'write'],
  },
};

const AuthContext = createContext(null);

export { AuthContext };

export function useAuth() {
  const user = useContext(AuthContext);

  const canRead = useCallback((resource) => {
    if (process.env.REACT_APP_AUTH_DISABLED === 'true') return true;
    return user?.permissions?.[resource]?.includes('read') ?? false;
  }, [user]);

  const canWrite = useCallback((resource) => {
    if (process.env.REACT_APP_AUTH_DISABLED === 'true') return true;
    return user?.permissions?.[resource]?.includes('write') ?? false;
  }, [user]);

  const can = useCallback((resource, action) => {
    if (process.env.REACT_APP_AUTH_DISABLED === 'true') return true;
    return user?.permissions?.[resource]?.includes(action) ?? false;
  }, [user]);

  // Base URL of the public registration site, served by GET /auth/me at runtime.
  // Not a REACT_APP_* build arg: dev and prod share one frontend image, so a
  // build-time value cannot differ per environment.
  const registrationBaseUrl = user?.registration_base_url || DEFAULT_REGISTRATION_BASE_URL;

  // Environment gate for audience targeting + invitation sends, also served by /auth/me.
  // Defaults to false when the field is absent so an old API build hides the UI rather
  // than showing a send button whose route will 403 — the safe direction for a feature
  // whose next action is email. Removed when FAC RBAC group mapping lands.
  const audienceWriteEnabled = user?.audience_write_enabled === true;

  const hubUrl = user?.hub_url || DEFAULT_HUB_URL;

  return { user, canRead, canWrite, can, registrationBaseUrl, hubUrl, audienceWriteEnabled };
}

/**
 * AuthGate — blocks rendering of children until authentication is confirmed.
 *
 * - Calls GET /auth/me on mount.
 * - 200 → stores user, renders children.
 * - 401 → redirects to SAML login immediately (nothing else renders).
 * - REACT_APP_AUTH_DISABLED=true → bypasses check (local dev).
 *
 * Provides user object to descendants via AuthContext.
 */
export default function AuthGate({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (process.env.REACT_APP_AUTH_DISABLED === 'true') {
      setUser(DEV_USER);
      setReady(true);
      return;
    }

    const loginUrl = `${API_BASE_URL}/auth/saml?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`;

    fetch(`${API_BASE_URL}/auth/me`, { credentials: 'include' })
      .then(r => {
        if (r.status === 401) {
          window.location.href = loginUrl;
          // Don't set ready — keep showing loading while browser redirects
          return null;
        }
        return r.ok ? r.json() : null;
      })
      .then(data => {
        if (data) {
          setUser(data);
          setReady(true);
        }
      })
      .catch(() => {
        // Network error — still redirect to SAML as fallback
        window.location.href = loginUrl;
      });
  }, []);

  if (!ready) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AuthContext.Provider value={user}>
      {children}
    </AuthContext.Provider>
  );
}
